import { AppInitialisation } from '@/domain/Sharing'

type Resolvers = {
    resolveLink: (req: { slug: string }) => Promise<{ path: string }>
    resolveReferralCode: (req: {
        code: string
    }) => Promise<{ landingPath: string; referrerLogin: string }>
}

/**
 * `retry`: a lookup failed for a reason that may pass (network, 5xx, 429),
 * so the caller should keep the start state and try again later.
 */
export type StartTarget = { path?: string; invitedBy?: string; retry?: boolean }

/** A failure worth retrying: no response at all, or a 5xx / 429 one. */
function isTransient(error: unknown): boolean {
    const status = (error as { response?: { status?: unknown } })?.response
        ?.status
    if (typeof status !== 'number') return true
    return status >= 500 || status === 429
}

function safePath(p: string | undefined): string | undefined {
    return p && p.startsWith('/') && !p.startsWith('//') ? p : undefined
}

/**
 * Where a shared link should take the user: its own path, a tracked
 * link's path, or a referral program's landing page (with who invited
 * them). Lookup failures leave the user where they are; a transient one
 * is flagged `retry`.
 */
export async function resolveStartTarget(
    init: AppInitialisation,
    api: Resolvers
): Promise<StartTarget> {
    const target: StartTarget = { path: safePath(init.t) }
    try {
        if (!target.path && init.l) {
            target.path = safePath(
                (await api.resolveLink({ slug: init.l })).path
            )
        }
        if (init.k) {
            const info = await api.resolveReferralCode({ code: init.k })
            target.invitedBy = info.referrerLogin || undefined
            target.path = target.path ?? safePath(info.landingPath)
        }
    } catch (error) {
        console.error('Failed to resolve the shared link:', error)
        if (isTransient(error)) target.retry = true
    }
    return target
}
